import { useState, useEffect } from 'react'
import RelativeSvgLoader from '../common/components/RelativeSvgLoader';
import YesNoPopup from '../common/components/YesNoPopup';
import Error from  '../common/components/ErrorMessage'
import Success from  '../common/components/SuccessMessage'
import {DELIVERY_OPTIONS_FUNCTION} from '../common/ResourcePaths';
import AuthData from '../common/AuthData';
import Fetch from '../services/fetchService';
import Post from '../services/postService';
import { FaTrash, FaGripVertical } from 'react-icons/fa';

function Delivery(){
    const [loader, setLoader] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [successTimer, setSuccessTimer] = useState(5);
    const [showPopup, setShowPopup] = useState(false);
    const [modified, setModified] = useState(false);

    // Delivery options state - array of objects with name and price
    const [deliveryOptions, setDeliveryOptions] = useState([]);

    // Validation states - individual errors for each option
    const [fieldErrors, setFieldErrors] = useState([]);

    // Drag and drop state
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    useEffect(() => {
        const loadDeliveryOptions = async () => {
            try {
                setLoader(true);
                const authData = AuthData.getAuthData();
                const url = `${authData.appFunctionUrl}${DELIVERY_OPTIONS_FUNCTION}?code=${authData.azureFunctionKey}`;
                const response = await Fetch(url, authData.clientId);
                const options = response || [];
                setDeliveryOptions(options);
                // Initialize fieldErrors to match the length of deliveryOptions
                setFieldErrors(options.map(() => ({ name: '', price: '' })));
            } catch (error) {
                console.error("Error loading delivery options:", error);
                // Set empty state if API fails
                setDeliveryOptions([]);
                setFieldErrors([]);
            } finally {
                setLoader(false);
            }
        };

        loadDeliveryOptions();
    }, []);

    // Validation helpers
    const validateDeliveryOptionName = (value, index) => {
        if (!value.trim()) return `Name is required for delivery option ${index + 1}.`;
        if (value.length > 50) return `Name must be less than 50 characters for delivery option ${index + 1}.`;
        return '';
    };

    const validateDeliveryOptionPrice = (value, index) => {
        if (value === '' || value === null) return `Price is required for delivery option ${index + 1}.`;
        const price = parseFloat(value);
        if (isNaN(price)) return `Price must be a valid number for delivery option ${index + 1}.`;
        if (price < 0) return `Price cannot be negative for delivery option ${index + 1}.`;
        if (price > 999) return `Price must be 999 or less for delivery option ${index + 1}.`;
        return '';
    };

    // Check if all delivery options are complete (both name and price filled)
    const areAllOptionsComplete = () => {
        return deliveryOptions.every(option =>
            option && option.name && option.name.trim() !== '' && option.price !== '' && option.price !== null
        );
    };

    // Handlers
    const handleOptionChange = (index, field, value) => {
        const updatedOptions = [...deliveryOptions];
        updatedOptions[index][field] = value;
        setDeliveryOptions(updatedOptions);
        setModified(true);

        // Validate this specific field in real-time
        const updatedErrors = [...fieldErrors];
        if (!updatedErrors[index]) {
            updatedErrors[index] = { name: '', price: '' };
        }

        if (field === 'name') {
            updatedErrors[index].name = validateDeliveryOptionName(value, index);
        } else if (field === 'price') {
            updatedErrors[index].price = validateDeliveryOptionPrice(value, index);
        }

        setFieldErrors(updatedErrors);
    };

    const addDeliveryOption = () => {
        setDeliveryOptions([...deliveryOptions, { name: '', price: '' }]);
        setFieldErrors([...fieldErrors, { name: '', price: '' }]);
        setModified(true);
    };

    const removeDeliveryOption = (index) => {
        const updatedOptions = deliveryOptions.filter((_, i) => i !== index);
        const updatedErrors = fieldErrors.filter((_, i) => i !== index);
        setDeliveryOptions(updatedOptions);
        setFieldErrors(updatedErrors);
        setModified(true);
    };

    const saveHandler = async (e) => {
        e.preventDefault();

        setLoader(true);
        setError(null);
        setSuccess(null);

        try {
            const authData = AuthData.getAuthData();
            const url = `${authData.appFunctionUrl}${DELIVERY_OPTIONS_FUNCTION}?code=${authData.azureFunctionKey}`;
            const options = deliveryOptions.map((option, index) => ({
                ...option,
                price: parseFloat(option.price) || 0,
                order: index // Add order property based on current position
            }));
            await Post(url, authData.clientId, options);

            setSuccess(true);
            setModified(false);
            setSuccessTimer(10);
            const timerInterval = setInterval(() => {
                setSuccessTimer((prev) => {
                    if (prev <= 1) {
                        clearInterval(timerInterval);
                        setSuccess(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (err) {
            console.error("Error saving delivery options:", err);
            setError('An error occurred while saving delivery options.');
        } finally {
            setLoader(false);
        }
    };

    const handleYes = () => {
        setModified(true);
        setDeliveryOptions([]);
        setFieldErrors([]);
        setDraggedIndex(null);
        setDragOverIndex(null);
        setShowPopup(false);
        setError(null);
        setSuccess(null);
    };

    const handleNo = () => {
        setShowPopup(false);
    };

    // Drag and drop handlers
    const handleDragStart = (e, index) => {
        // Prevent dragging if the target is an input field
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
            e.preventDefault();
            return;
        }
        
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', ''); // Required for Firefox
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnter = (e, index) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOverIndex(null);
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        const dragIndex = draggedIndex;
        if (dragIndex === null || dragIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        // Reorder the arrays
        const newDeliveryOptions = [...deliveryOptions];
        const newFieldErrors = [...fieldErrors];

        // Move the dragged item to the new position
        const [draggedOption] = newDeliveryOptions.splice(dragIndex, 1);
        const [draggedError] = newFieldErrors.splice(dragIndex, 1);

        newDeliveryOptions.splice(dropIndex, 0, draggedOption);
        newFieldErrors.splice(dropIndex, 0, draggedError);

        setDeliveryOptions(newDeliveryOptions);
        setFieldErrors(newFieldErrors);
        setModified(true);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    return (
        <div className="container" style={{ position: 'relative' }}>
            {loader && <RelativeSvgLoader />}
            <div className="form-wrapper" style={{ opacity: loader ? 0.3 : 1 }}>
                {/* Show combined error box above all fields */}
                {(fieldErrors.some(optionErrors => optionErrors && (optionErrors.name || optionErrors.price)) || error) && (
                    <Error message={
                        [
                            error,
                            ...fieldErrors.flatMap(optionErrors => optionErrors ? [optionErrors.name, optionErrors.price] : [])
                        ]
                            .filter(Boolean)
                            .join(' ')
                    } />
                )}
                {success && <Success message={`Delivery options saved successfully! (${successTimer}s)`} />}

                <form onSubmit={saveHandler}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '20px' }}>
                        <button
                            type="button"
                            onClick={addDeliveryOption}
                            style={{
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'background-color 0.3s ease'
                            }}
                        >
                            + Add Option
                        </button>
                    </div>

                    {deliveryOptions.map((option, index) => (
                        <div 
                            key={index}
                            draggable={deliveryOptions.length > 1}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={handleDragOver}
                            onDragEnter={(e) => handleDragEnter(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            onMouseEnter={(e) => {
                                if (deliveryOptions.length > 1) {
                                    e.currentTarget.style.cursor = 'grab';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (deliveryOptions.length > 1) {
                                    e.currentTarget.style.cursor = 'grab';
                                    e.currentTarget.style.boxShadow = 'none';
                                }
                            }}
                            style={{
                                padding: '10px',
                                marginBottom: '10px',
                                border: draggedIndex === index ? '2px dashed #007bff' : 
                                       dragOverIndex === index ? '2px solid #28a745' : '1px solid #ddd',
                                borderRadius: '5px',
                                backgroundColor: draggedIndex === index ? '#f8f9fa' : 
                                               dragOverIndex === index ? '#e8f5e8' : 'white',
                                cursor: deliveryOptions.length > 1 ? 'grab' : 'default',
                                opacity: draggedIndex === index ? 0.5 : 1,
                                userSelect: 'none', // Prevent text selection during drag
                                transition: 'all 0.2s ease',
                                boxShadow: 'none'
                            }}
                        >
                            <div className="form-grid">
                                <label className="form-label">Delivery Option {index + 1}:</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <FaGripVertical 
                                        style={{ 
                                            cursor: deliveryOptions.length > 1 ? 'grab' : 'not-allowed', 
                                            color: deliveryOptions.length > 1 ? '#6c757d' : '#adb5bd',
                                            fontSize: '16px'
                                        }}
                                        title={deliveryOptions.length > 1 ? "Drag to reorder" : "Add more options to enable reordering"}
                                    />
                                    <input
                                        type="text"
                                        value={option.name}
                                        onChange={(e) => handleOptionChange(index, 'name', e.target.value)}
                                        className="input-field"
                                        placeholder="e.g., Standard Delivery"
                                        style={{ flex: 1 }}
                                        maxLength={50}
                                    />
                                    <FaTrash
                                        style={{ 
                                            cursor: 'pointer', 
                                            color: '#dc3545',
                                            fontSize: '16px'
                                        }}
                                        onClick={() => removeDeliveryOption(index)}
                                        title="Remove this option"
                                    />
                                </div>

                                <label className="form-label">Price:</label>
                                <input
                                    type="number"
                                    value={option.price}
                                    onChange={(e) => handleOptionChange(index, 'price', e.target.value)}
                                    className="input-field"
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    max="999"
                                />
                            </div>
                            {index < deliveryOptions.length - 1 && (
                                <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid #ddd' }} />
                            )}
                        </div>
                    ))}

                    <div className='form-footer'>
                        <button
                            type="submit"
                            disabled={
                                !modified ||
                                fieldErrors.some(optionErrors => optionErrors && (optionErrors.name || optionErrors.price)) ||
                                !areAllOptionsComplete()
                            }
                            className="submit-button"
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            className='clear-data-button'
                            style={{ marginLeft: '10px' }}
                            onClick={() => setShowPopup(true)}
                        >
                            Reset
                        </button>
                    </div>
                </form>
            </div>

            {showPopup && (
                <YesNoPopup
                    handleYes={handleYes}
                    handleNo={handleNo}
                />
            )}
        </div>
    );
}

export default Delivery;
